import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import giftBoxImage from "@/assets/gift-box-original-transparent.webp";
import { SparkSyncGame } from "@/components/soul-sync/SparkSyncGame";

type Msg = { role: "user" | "assistant"; content: string };

interface GiftConciergeProps {
  profileId?: string;
  recipientName?: string;
  giverName?: string;
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gift-concierge`;
const SURPRISE_GIFT_GAME_PROMPT = "Start the Surprise Gift Game";
const SPARK_SYNC_PROMPT = "Begin Spark-Sync";
const conciergeAvatar = "/images/rebecca-gift-concierge.webp";
const GUEST_REMINDER = "Rebecca is magic, but she is not a mind reader! Set up a profile to save this Spark.";

const shouldLaunchSparkSyncFromConversation = (text: string) => {
  const normalized = text.trim().toLowerCase();

  return [
    "spark-sync",
    "spark sync",
    "start the game",
    "start the spark",
    "surprise gift game",
    "play the game",
    "begin the game",
    "help me choose",
  ].some((phrase) => normalized.includes(phrase));
};

const FUN_FACTS = [
  "🎁 Did you know? Australians spend over $32 billion on gifts each year — and roughly $5 billion of that ends up unused or regifted!",
  "🛍️ The average Aussie spends 16 hours per year shopping for gifts. That's two full working days of wandering through shops!",
  "🎄 In Australia, 'Kris Kringle' is the most searched gift-related term in November. The rest of the world calls it 'Secret Santa'!",
  "💡 Research shows that experiential gifts create 3x more happiness than material ones. Maybe add a voucher to your wishlist?",
  "🎀 The tradition of wrapping gifts in paper only started in the early 1900s — before that, gifts were wrapped in cloth or tissue!",
  "📦 Australians generate 30% more waste during the holiday season. That's over 150,000 tonnes of wrapping paper alone!",
  "🐨 Australia is one of the only countries where 'Boxing Day' shopping is a bigger event than Black Friday!",
  "💝 Studies show the best gifts aren't the most expensive — they're the most thoughtful. A $20 gift chosen with care beats a $200 generic one!",
  "🌏 The world's most expensive gift ever given? A $46 million jet. We think a heartfelt Hintsly wishlist is more romantic, though.",
  "🎶 'Jingle Bells' was originally written for Thanksgiving, not Christmas. Imagine getting your gift timing that wrong!",
];

const GiftConcierge = ({ profileId, recipientName, giverName }: GiftConciergeProps) => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showGiftThrow, setShowGiftThrow] = useState(false);
  const [giftBoxes, setGiftBoxes] = useState<Array<{ id: number; startX: number; delay: number; drift: number; spinDir: number; exitDir: number; bounceHeight: number; isJoker: boolean }>>([]); 
  const [showRibbon, setShowRibbon] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [sparkSyncActive, setSparkSyncActive] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fatigueTriggeredRef = useRef(false);

  const metadataName = typeof user?.user_metadata?.full_name === "string" ? user.user_metadata.full_name : null;
  const firstName = (value?: string | null) => value?.trim().split(/\s+/)[0] ?? null;
  const conciergeName = firstName(giverName) ?? firstName(displayName) ?? firstName(metadataName) ?? firstName(user?.email ?? null);

  const getGuestLoginPrompt = useCallback(
    () =>
      `Welcome. I’m Rebecca, Hintsly’s gifting concierge. Before we begin the **Surprise Gift Game**, please **Create a Login to save your Spark**. Once you’re signed in, I can keep your ideas elegant, organised, and beautifully timed.\n\n${GUEST_REMINDER}`,
    []
  );

  const getGreeting = useCallback(
    (source: "default" | "hero-box" | "fatigue-scroll") => {
      if (!user) {
        if (source === "hero-box") {
          return `${getGuestLoginPrompt()}\n\nThe box has already made the introduction — all that remains is your entrance.`;
        }

        if (source === "fatigue-scroll") {
          return `${getGuestLoginPrompt()}\n\nIf the choices are starting to blur together, that is precisely when I should intervene. Allow me to cut through the noise before decision fatigue settles in.`;
        }

        return "Welcome. I’m Rebecca, your gifting concierge. If you’d like to browse with intention, I can help — and before we begin the **Surprise Gift Game**, please **Create a Login to save your Spark**.";
      }

      const salutation = source === "fatigue-scroll"
        ? `I noticed you’ve been exploring with admirable patience, ${conciergeName ?? "there"}.`
        : source === "hero-box"
          ? `A discerning choice, ${conciergeName ?? "there"}.`
          : `Delighted to see you, ${conciergeName ?? "there"}.`;

      if (source === "fatigue-scroll") {
        return `${salutation} If the options are beginning to feel repetitive, allow me to narrow the field with elegance. I can refine your ideas, curate thoughtful options, and shape a Spark worth sending.`;
      }

      return `${salutation} I’m Rebecca, your gifting concierge. I can refine your ideas, curate thoughtful options, and help you shape a Spark worth sending.`;
    },
    [conciergeName, getGuestLoginPrompt, user]
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    let active = true;

    const resolveDisplayName = async () => {
      if (!user?.id || giverName) {
        setDisplayName(null);
        return;
      }

      setDisplayName(metadataName ?? user.email ?? null);

      const { data } = await supabase
        .from("profiles")
        .select("display_name")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!active) return;

      if (data?.display_name) {
        setDisplayName(data.display_name);
      }
    };

    resolveDisplayName();

    return () => {
      active = false;
    };
  }, [giverName, metadataName, user]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
      if (messages.length === 0) {
        setMessages([{ role: "assistant", content: getGreeting("default") }]);
      }
    }
  }, [getGreeting, messages.length, open]);

  const openRebecca = useCallback(
    (source: "hero-box" | "fatigue-scroll") => {
      setSparkSyncActive(false);
      setMessages([{ role: "assistant", content: getGreeting(source) }]);
      setOpen(true);
    },
    [getGreeting]
  );

  // Listen for "bored mode" trigger from header
  const handleBoredMode = useCallback(() => {
    const randomFact = FUN_FACTS[Math.floor(Math.random() * FUN_FACTS.length)];
    const isMobileViewport = window.matchMedia("(max-width: 767px)").matches;

    // Generate a random number of gift boxes (3–8) with varied properties
    const count = isMobileViewport ? 2 + Math.floor(Math.random() * 3) : 3 + Math.floor(Math.random() * 6);
    const jokerIndex = Math.floor(Math.random() * count); // one gets the smiley treatment
    const boxes = Array.from({ length: count }, (_, i) => ({
      id: Date.now() + i,
      startX: 5 + Math.random() * 85,
      delay: Math.random() * 1.5,
      drift: (Math.random() - 0.5) * 200,
      spinDir: Math.random() > 0.5 ? 1 : -1,
      exitDir: Math.random() > 0.5 ? 1 : -1,
      bounceHeight: 20 + Math.random() * 30, // vh — how high the first bounce goes (varies a lot)
      isJoker: i === jokerIndex,
    }));
    setGiftBoxes(boxes);
    setShowGiftThrow(true);
    setShowRibbon(true);
    setTimeout(() => setShowGiftThrow(false), 8000);
    setTimeout(() => setShowRibbon(false), 5000);

    // Open Rebecca with fun fact
    setMessages([
      {
        role: "assistant",
        content: user
          ? `A little diversion, ${conciergeName ?? "there"}.\n\n${randomFact}\n\nIf you wish, I can now turn that passing curiosity into a beautifully judged gift idea.`
            : `${randomFact}\n\nAnd when you’re ready for something more tailored, please **Create a Login to save your Spark** before we begin **Spark-Sync**.\n\n${GUEST_REMINDER}`,
      },
    ]);
      setSparkSyncActive(false);
    setOpen(true);
  }, [conciergeName, user]);

  const handleRebeccaOpen = useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent<{ source?: "hero-box" | "fatigue-scroll" }>;
      const source = customEvent.detail?.source === "fatigue-scroll" ? "fatigue-scroll" : "hero-box";
      openRebecca(source);
    },
    [openRebecca]
  );

  useEffect(() => {
    window.addEventListener("hintsly:bored-mode", handleBoredMode);
    window.addEventListener("hintsly:rebecca-open", handleRebeccaOpen as EventListener);
    return () => {
      window.removeEventListener("hintsly:bored-mode", handleBoredMode);
      window.removeEventListener("hintsly:rebecca-open", handleRebeccaOpen as EventListener);
    };
  }, [handleBoredMode, handleRebeccaOpen]);

  useEffect(() => {
    if (location.pathname !== "/") {
      fatigueTriggeredRef.current = false;
      return;
    }

    const handleScroll = () => {
      if (fatigueTriggeredRef.current || open) return;

      const sections = Array.from(document.querySelectorAll("main section"));
      if (sections.length < 3) {
        const depthThreshold = window.innerHeight * 2.4;
        if (window.scrollY >= depthThreshold) {
          fatigueTriggeredRef.current = true;
          window.dispatchEvent(new CustomEvent("hintsly:rebecca-open", { detail: { source: "fatigue-scroll" } }));
        }
        return;
      }

      const viewedSections = sections.filter((section) => section.getBoundingClientRect().top <= window.innerHeight * 0.55).length;

      if (viewedSections >= 3) {
        fatigueTriggeredRef.current = true;
        window.dispatchEvent(new CustomEvent("hintsly:rebecca-open", { detail: { source: "fatigue-scroll" } }));
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname, open]);

  const promptLoginForGame = useCallback(() => {
    setSparkSyncActive(false);
    setMessages([{ role: "assistant", content: getGuestLoginPrompt() }]);
    setOpen(true);
  }, [getGuestLoginPrompt]);

  const startSparkSync = useCallback(() => {
    if (!user) {
      promptLoginForGame();
      return;
    }

    setSparkSyncActive(true);
    setMessages([
      {
        role: "assistant",
        content: `Certainly${conciergeName ? `, ${conciergeName}` : ""}. We shall compose this Spark with precision. I’ll begin by understanding who it is for, then I’ll refine the logistics, region, and emotional reason before revealing five beautifully judged directions.`,
      },
    ]);
    setOpen(true);
  }, [conciergeName, promptLoginForGame, user]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return;

    const trimmedText = text.trim();

    if (
      trimmedText === SPARK_SYNC_PROMPT ||
      trimmedText === SURPRISE_GIFT_GAME_PROMPT ||
      shouldLaunchSparkSyncFromConversation(trimmedText)
    ) {
      startSparkSync();
      return;
    }

    setSparkSyncActive(false);

    const userMsg: Msg = { role: "user", content: trimmedText };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);

    let assistantSoFar = "";

    const upsertAssistant = (chunk: string) => {
      assistantSoFar += chunk;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          return prev.map((m, i) =>
            i === prev.length - 1 ? { ...m, content: assistantSoFar } : m
          );
        }
        return [...prev, { role: "assistant", content: assistantSoFar }];
      });
    };

    try {
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: [...messages, userMsg],
          profileId: profileId || undefined,
          giverName: giverName || undefined,
        }),
      });

      if (resp.status === 429) {
        toast.error("Too many requests — please wait a moment and try again.");
        setIsLoading(false);
        return;
      }
      if (resp.status === 402) {
        toast.error("AI service credits exhausted. Please try again later.");
        setIsLoading(false);
        return;
      }
      if (!resp.ok || !resp.body) {
        throw new Error("Failed to connect to Gift Concierge");
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let streamDone = false;

      while (!streamDone) {
        const { value, done } = await reader.read();
        if (done) {
          streamDone = true;
          break;
        }

        textBuffer += decoder.decode(value, { stream: true });
        const lines = textBuffer.split("\n");
        textBuffer = lines.pop() || "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith("data: ")) continue;
          const jsonStr = trimmed.slice(6);
          if (jsonStr === "[DONE]") continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) upsertAssistant(content);
          } catch { /* ignore */ }
        }
      }
    } catch {
      toast.error("Couldn't reach the Gift Concierge. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const quickPrompts = recipientName
    ? [
        SPARK_SYNC_PROMPT,
        `What would ${recipientName} love for their birthday?`,
        "What should I avoid buying?",
        "Suggest something under $50",
        "What sizes should I know about?",
      ]
    : [
        SPARK_SYNC_PROMPT,
        "Help me find a gift for a friend",
        "Gift ideas under $50",
        "What's trending in gifts right now?",
        "How does Hintsly work?",
      ];

  return (
    <>
      {/* Gift boxes rain from the top, bounce, exit */}
      <AnimatePresence>
        {showGiftThrow && giftBoxes.map((box) => {
          const bounceBottom = 65 + Math.random() * 15; // vh where it "lands"
          const bounceH1 = bounceBottom - box.bounceHeight; // big bounce — varies per box
          const bounceH2 = bounceBottom - box.bounceHeight * 0.35; // smaller second bounce
          const bounceH3 = bounceBottom - box.bounceHeight * 0.12; // tiny third bounce
          const yPx = (pct: number) => (pct / 100) * window.innerHeight;

          const duration = 5.5 + Math.random() * 1.5; // 5.5–7s per box

          return (
            <motion.div
              key={box.id}
              className="fixed pointer-events-none"
              style={{
                left: `${box.startX}vw`,
                top: 0,
                zIndex: 9999,
                width: "clamp(64px, 10vw, 120px)",
                height: "clamp(64px, 10vw, 120px)",
              }}
              initial={{ y: -150, opacity: 0, scale: 0.3 }}
              animate={{
                y: [
                  -150,
                  yPx(bounceBottom),
                  yPx(bounceH1),
                  yPx(bounceBottom),
                  yPx(bounceH2),
                  yPx(bounceBottom),
                  yPx(bounceH3),
                  yPx(bounceBottom),
                  yPx(bounceBottom) + 30 * box.exitDir,
                  window.innerHeight + 200,
                ],
                x: [0, box.drift * 0.2, box.drift * 0.5, box.drift * 0.3, box.drift * 0.7, box.drift * 0.5, box.drift * 0.9, box.drift, box.drift + 80 * box.exitDir, box.drift + 250 * box.exitDir],
                rotate: [0, 20 * box.spinDir, -15 * box.spinDir, 10 * box.spinDir, -12 * box.spinDir, 8 * box.spinDir, -5 * box.spinDir, 3 * box.spinDir, 30 * box.spinDir, 60 * box.spinDir],
                scale: [0.3, 1, 1.2, 1, 1.1, 1, 1.05, 1, 0.9, 0.3],
                opacity: [0, 1, 1, 1, 1, 1, 1, 1, 0.6, 0],
              }}
              transition={{
                duration,
                delay: box.delay,
                ease: [0.2, 0, 0.4, 1],
                times: [0, 0.18, 0.3, 0.4, 0.5, 0.58, 0.65, 0.72, 0.86, 1],
              }}
              exit={{ opacity: 0 }}
            >
              {box.isJoker ? (
                /* Joker box: inverted colours with a smiley face */
                <div className="relative w-full h-full">
                  <img
                    src={giftBoxImage}
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full object-contain drop-shadow-2xl"
                    style={{ filter: "hue-rotate(180deg) saturate(1.3)" }}
                  />
                  <span
                    className="absolute inset-0 flex items-center justify-center text-3xl"
                    style={{ paddingTop: "15%" }}
                  >
                    😜
                  </span>
                </div>
              ) : (
                <img
                  src={giftBoxImage}
                    alt=""
                    aria-hidden="true"
                  className="w-full h-full object-contain drop-shadow-2xl"
                />
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Ribbon swirl animation — wavy SVG ribbons */}
      <AnimatePresence>
        {showRibbon && (
          <motion.div
            className="fixed inset-0 z-[99] pointer-events-none overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* First ribbon — top wave */}
            <motion.svg
              viewBox="0 0 1200 120"
              className="absolute w-[200vw] h-24"
              style={{ top: "25%" }}
              initial={{ x: "-100vw" }}
              animate={{ x: "100vw" }}
              transition={{ duration: 2.5, ease: "easeInOut" }}
            >
              <path
                d="M0,60 C100,20 200,100 300,50 C400,0 500,90 600,60 C700,30 800,100 900,50 C1000,0 1100,80 1200,60"
                fill="none"
                stroke="hsl(var(--destructive))"
                strokeWidth="6"
                strokeLinecap="round"
                opacity="0.8"
              />
              <path
                d="M0,60 C100,20 200,100 300,50 C400,0 500,90 600,60 C700,30 800,100 900,50 C1000,0 1100,80 1200,60"
                fill="none"
                stroke="hsl(var(--destructive))"
                strokeWidth="14"
                strokeLinecap="round"
                opacity="0.15"
              />
            </motion.svg>

            {/* Second ribbon — lower wave, opposite direction */}
            <motion.svg
              viewBox="0 0 1200 120"
              className="absolute w-[180vw] h-20"
              style={{ top: "55%" }}
              initial={{ x: "100vw" }}
              animate={{ x: "-100vw" }}
              transition={{ duration: 2.2, ease: "easeInOut", delay: 0.3 }}
            >
              <path
                d="M0,60 C80,100 180,10 280,70 C380,120 480,20 580,60 C680,100 780,10 880,70 C980,120 1080,30 1200,60"
                fill="none"
                stroke="hsl(var(--destructive))"
                strokeWidth="5"
                strokeLinecap="round"
                opacity="0.7"
              />
              <path
                d="M0,60 C80,100 180,10 280,70 C380,120 480,20 580,60 C680,100 780,10 880,70 C980,120 1080,30 1200,60"
                fill="none"
                stroke="hsl(var(--destructive))"
                strokeWidth="12"
                strokeLinecap="round"
                opacity="0.12"
              />
            </motion.svg>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating trigger button */}
      <AnimatePresence>
        {!open && (
          <div className="fixed bottom-20 right-4 z-50 flex max-w-[calc(100vw-2rem)] flex-col items-end gap-2 lg:bottom-6">
            {!user && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                className="max-w-xs rounded-2xl border border-border bg-black/95 px-4 py-3 text-xs leading-relaxed text-muted-foreground shadow-xl"
              >
                <span className="font-semibold text-foreground">Create a login to save your Spark.</span>{" "}
                {GUEST_REMINDER}
              </motion.div>
            )}
            <motion.button
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              onClick={() => {
                setSparkSyncActive(false);
                setOpen(true);
              }}
              className="flex items-center gap-2 rounded-full bg-primary px-3 py-2 text-primary-foreground shadow-lg transition-shadow hover:shadow-xl"
            >
              <img src={conciergeAvatar} alt="Rebecca, Hintsly gifting concierge" className="h-8 w-8 rounded-full object-cover border-2 border-primary-foreground/30" loading="lazy" width={32} height={32} />
              <span className="hidden text-sm font-medium sm:inline">Rebecca</span>
            </motion.button>
          </div>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="fixed bottom-20 lg:bottom-6 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] bg-black border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            style={{ height: "min(520px, calc(100vh - 8rem))" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-black">
              <div className="flex items-center gap-2">
                  <img src={conciergeAvatar} alt="Rebecca, Hintsly gifting concierge" className="w-9 h-9 rounded-full object-cover border-2 border-primary/20" loading="lazy" width={36} height={36} />
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Rebecca</h3>
                  <p className="text-[10px] text-muted-foreground">
                    {recipientName ? `Concierge guidance for ${recipientName}` : "Private concierge for thoughtful gifting"}
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {messages.length === 0 && (
                <div className="text-center py-6 space-y-4">
                  <Sparkles className="h-8 w-8 text-primary/40 mx-auto" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Allow Rebecca to curate the next step
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {recipientName ? `I can interpret ${recipientName}’s preferences, sizing, and no-go zones with discretion.` : "I can shape a more elegant gifting decision, from the first idea to the final Spark."}
                    </p>
                  </div>
                  {!user && (
                    <div className="rounded-xl border border-secondary/30 bg-secondary/10 p-4 text-left">
                      <p className="text-sm font-medium text-foreground">Create a Login to save your Spark</p>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        Guests are welcome to browse, but Rebecca reserves Spark-Sync for signed-in guests with somewhere elegant to keep their ideas. {GUEST_REMINDER}
                      </p>
                      <Button asChild size="sm" variant="secondary" className="mt-3">
                        <Link to="/auth">Create a login</Link>
                      </Button>
                    </div>
                  )}
                  <div className="grid gap-2">
                    {quickPrompts.map((prompt) => (
                      <button
                        key={prompt}
                        onClick={() => sendMessage(prompt)}
                        className="text-xs text-left px-3 py-2 rounded-lg border border-border hover:bg-accent hover:text-accent-foreground transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

                {sparkSyncActive && user && <SparkSyncGame conciergeName={conciergeName} />}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : "bg-muted text-foreground rounded-bl-md"
                    }`}
                  >
                    {msg.role === "assistant" ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:mb-2 [&>ul]:pl-4">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <p>{msg.content}</p>
                    )}
                  </div>
                </div>
              ))}

              {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
             {!sparkSyncActive && (
               <form onSubmit={handleSubmit} className="p-3 border-t border-border flex gap-2">
                 <Input
                   ref={inputRef}
                   value={input}
                   onChange={(e) => setInput(e.target.value)}
                   placeholder="Ask Rebecca to curate a gift..."
                   disabled={isLoading}
                   className="text-sm"
                 />
                 <Button type="submit" size="icon" disabled={isLoading || !input.trim()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                   <Send className="h-4 w-4" />
                 </Button>
               </form>
             )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default GiftConcierge;
